import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { useFixedDaysStore } from '@/store/fixedDaysStore';
import { FixedDaysBar } from '@/components/fixedDays/FixedDaysBar';
import { Button } from '@/components/ui';
import type { FixedDaysSummary, FixedDaysPool } from '@/types/fixedDays.types';
import { DragHandle, type DragHandleProps } from './DraggableWidget';

export function FixedDaysWidget({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) {
  const {
    pools,
    summaries,
    histories,
    selectedPool,
    loading,
    loadingHistories,
    fetchPools,
    fetchHistories,
    fetchSummary,
    setSelectedPool,
  } = useFixedDaysStore();
  const [isInitializingData, setIsInitializingData] = useState(true);

  // Fetch pools on mount
  useEffect(() => {
    fetchPools();
  }, [fetchPools]);

  useEffect(() => {
    setIsInitializingData(true);
  }, [selectedPool?.poolId]);

  // Set selected pool to first pool on mount
  useEffect(() => {
    if (pools.length > 0 && !selectedPool) {
      setSelectedPool(pools[0]);
    }
  }, [pools, selectedPool, setSelectedPool]);

  // Fetch histories when pool changes
  useEffect(() => {
    if (selectedPool) {
      fetchHistories(selectedPool.poolId);
    }
  }, [selectedPool, fetchHistories]);

  // Fetch summary when histories are loaded (use first history item)
  useEffect(() => {
    if (histories.length > 0) {
      const firstHistory = histories[0];
      fetchSummary(firstHistory.uniqueFileID)
        .catch(() => {
          // Error is handled in store state
        })
        .finally(() => {
          setIsInitializingData(false);
        });
    }
  }, [histories, fetchSummary]);

  useEffect(() => {
    if (selectedPool && !loadingHistories && histories.length === 0) {
      setIsInitializingData(false);
    }
  }, [selectedPool, loadingHistories, histories.length]);

  // Clear initializing state when pool fetch completes but returned no pools —
  // selectedPool will never be set so the effect above cannot fire.
  useEffect(() => {
    if (!loading && pools.length === 0) {
      setIsInitializingData(false);
    }
  }, [loading, pools.length]);

  // Filter to show 6 months based on new Fixed Days display rules
  const monthlyData = useMemo((): FixedDaysSummary[] => {
    const DISPLAY_COUNT = 6;
    const data = summaries.filter((item) => item.sectionName.toLowerCase() === 'month');

    if (data.length === 0) return [];

    // 1. Find the first index where percent is less than 100 for months
    let startIndex = data.findIndex((item) => item.pwFixedPercent < 100);

    // 2. Default: If no month has percent < 100, start at the beginning
    if (startIndex === -1) {
      startIndex = 0;
    }

    // 3. Boundary Check: Always show 6 records.
    // If the "less than 100" occurs at the end, shift the window back.
    // Example: if index is 10 (November), we shift back to index 6 (July)
    if (startIndex > data.length - DISPLAY_COUNT) {
      startIndex = data.length - DISPLAY_COUNT;
    }

    // 4. Safety: Ensure startIndex doesn't go below 0 (for very small arrays)
    startIndex = Math.max(0, startIndex);

    // 5. Slice the actual objects for display
    const selectedRecords = data.slice(startIndex, startIndex + DISPLAY_COUNT);

    return selectedRecords;
  }, [summaries]);

  // Show On Hire columns if any summary row has non-zero onhire data
  const hasCmOnhire = useMemo(() => {
    return summaries.some(
      (s) =>
        s.cmOnhireDays > 0 ||
        (s.cmOnhireTce !== null && s.cmOnhireTce !== undefined && s.cmOnhireTce > 0)
    );
  }, [summaries]);

  const handleRefresh = () => {
    setIsInitializingData(true);
    fetchPools(true); // Force refresh
    if (selectedPool) {
      fetchHistories(selectedPool.poolId);
      // Summary will be fetched automatically by the useEffect when histories update
    }
  };

  const showLoadingState = loading || loadingHistories || isInitializingData;

  const handlePoolChange = (pool: FixedDaysPool) => {
    setSelectedPool(pool);
  };

  const handlePoolSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextPool = pools.find((pool) => pool.poolId.toString() === event.target.value);
    if (nextPool) {
      handlePoolChange(nextPool);
    }
  };

  return (
    <div className="h-full">
      {/* Header — same pattern as Recent Fixtures (title + actions one row) */}
      <div className="-mx-px -mt-px mb-2 flex items-center justify-between rounded-t-lg bg-gray-200/60 px-4 py-2 dark:bg-dark-700 sm:mb-2.5 sm:px-5 sm:py-2.5">
        <div className="min-w-0 pr-2">
          <h2 className="truncate text-base font-semibold text-gray-900 dark:text-white sm:text-lg">
            Fixed Days
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
          {dragHandleProps && <DragHandle {...dragHandleProps} />}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
            title="Refresh data"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Link to={selectedPool ? `/fixed-days?poolId=${selectedPool.poolId}` : '/fixed-days'}>
            <Button variant="ghost" size="sm" title="View all Fixed Days">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Pool Selector and Date */}
      <div className="mb-2 flex flex-col items-stretch gap-1.5 px-4 sm:mb-2.5 sm:flex-row sm:items-center sm:gap-2 sm:px-5 lg:grid lg:grid-cols-[30%_70%] lg:items-center">
        <div className="w-full sm:max-w-xs lg:max-w-none">
          <select
            id="fixed-days-pool-select"
            aria-label="Pool"
            value={selectedPool?.poolId.toString() || ''}
            onChange={handlePoolSelectChange}
            disabled={loading || pools.length === 0}
            className="block h-8 w-full cursor-pointer appearance-none rounded-md border border-gray-300 bg-white bg-right bg-no-repeat px-2 text-xs font-medium text-gray-900 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-50 dark:border-dark-600 dark:bg-dark-800 dark:text-white dark:disabled:bg-dark-900 sm:h-9 sm:rounded-lg sm:px-2.5 sm:text-sm lg:h-10 lg:px-3"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
              backgroundPosition: 'right 0.5rem center',
              backgroundSize: '1.25em 1.25em',
            }}
          >
            {pools.length === 0 && <option value="">No pools available</option>}
            {pools.map((pool) => (
              <option key={pool.poolId} value={pool.poolId.toString()}>
                {pool.pool}
              </option>
            ))}
          </select>
        </div>
        {histories.length > 0 && (
          <span className="line-clamp-2 text-center text-[10px] leading-snug text-gray-500 dark:text-gray-400 sm:text-xs lg:text-right lg:text-sm">
            {histories[0].linkName}
          </span>
        )}
      </div>

      {/* Monthly bars */}
      <div className="px-4 pb-4 sm:px-5 sm:pb-5">
        {showLoadingState && monthlyData.length === 0 ? (
          // Loading skeletons
          Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="mb-1 rounded-lg border border-gray-200 px-2 py-1.5 dark:border-dark-500 sm:mb-1.5 sm:px-3 sm:py-2 xl:px-3 xl:py-1.5"
            >
              <div className="grid gap-1.5 sm:gap-2 xl:grid-cols-12 xl:items-center">
                <div className="xl:col-span-3">
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200 dark:bg-dark-700 sm:h-5 sm:w-20" />
                </div>
                <div className="xl:col-span-3">
                  <div className="mb-0.5 h-2.5 w-full animate-pulse rounded bg-gray-100 dark:bg-dark-700 sm:mb-1 sm:h-3" />
                  <div className="mb-0.5 h-1 w-full animate-pulse rounded-full bg-gray-200 dark:bg-dark-700 sm:mb-1 sm:h-1.5" />
                  <div className="h-2.5 w-full animate-pulse rounded bg-gray-100 dark:bg-dark-700 sm:h-3" />
                </div>
                <div className="xl:col-span-6">
                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="h-8 animate-pulse rounded bg-gray-100 dark:bg-dark-700 sm:h-10" />
                    <div className="h-8 animate-pulse rounded bg-gray-100 dark:bg-dark-700 sm:h-10" />
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : monthlyData.length > 0 ? (
          monthlyData.map((item, idx) => (
            <FixedDaysBar
              key={item.sectionID || idx}
              data={item}
              title={item.rowLabel}
              hasCmOnhire={hasCmOnhire}
              isLoading={false}
              compact
            />
          ))
        ) : (
          <div className="flex min-h-[10rem] items-center justify-center px-2 py-8 text-center text-xs text-gray-500 dark:text-gray-400 sm:min-h-[14rem] sm:text-sm">
            {pools.length === 0 ? 'No pools available' : 'No monthly data available'}
          </div>
        )}
      </div>
    </div>
  );
}
